import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Page } from "shared/ui";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import type { CSSProperties } from "react";
import {
  MachineItem,
  useGetAllMachinesQuery,
  useGetUsersQuery,
} from "shared/api";
import TaskScheduleTable from "./TaskScheduleTable";
import { CreateTaskExecutionModal } from "./CreateTaskExecutionModal";
import { getThemeColor } from "./taskHubHelpers";

const TaskHubUserAllTasks = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const themeColor = getThemeColor();

  const [filterOneTimeTasks, setFilterOneTimeTasks] = useState(false);
  const [filterUnassignedTasks, setFilterUnassignedTasks] = useState(() => {
    const raw = searchParams.get("user");
    return !!raw?.trim() && raw.trim().toLowerCase() === "unassigned";
  });
  const [filterPendingTasks, setFilterPendingTasks] = useState(() => {
    const raw = searchParams.get("status");
    if (raw === null || raw === "") return true;
    return raw.toUpperCase() === "PENDING";
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  /** Active filter: green (intuitive “on”); inactive: outline in app theme */
  const filterActiveGreen = "#16a34a";
  const filterActiveGreenBorder = "#15803d";

  const filterToggleStyle = (active: boolean): CSSProperties =>
    active
      ? {
          backgroundColor: filterActiveGreen,
          borderColor: filterActiveGreenBorder,
          color: "#ffffff",
          fontWeight: 600,
        }
      : {
          backgroundColor: "#ffffff",
          borderColor: themeColor,
          color: themeColor,
          fontWeight: 500,
        };

  const { data: usersResponse } = useGetUsersQuery({ limit: 1000 });
  const allUsers = usersResponse?.results ?? [];
  const transformedUsers = allUsers.map((user) => ({
    label: user.username,
    value: user.id,
  }));

  const { data: machinesData } = useGetAllMachinesQuery({}) as {
    data: MachineItem[] | undefined;
  };

  const machineParam = searchParams.get("machine");
  const userParam = searchParams.get("user");

  const machineIdsFromUrl = useMemo(() => {
    if (!machineParam?.trim() || !machinesData) return [];
    const name = machineParam.trim();
    const found = machinesData.find((m) => m.name === name);
    return found ? [found.id] : [];
  }, [machineParam, machinesData]);

  const urlUserForTable =
    userParam?.trim() && userParam.trim().toLowerCase() !== "unassigned"
      ? userParam.trim()
      : undefined;

  return (
    <Page title={t("taskHub.taskHub") || "Task Hub"}>
      <div className="mb-4 p-3 flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg border border-gray-300 shadow-sm">
        <Button
          type="default"
          style={filterToggleStyle(filterOneTimeTasks)}
          onClick={() => setFilterOneTimeTasks((prev) => !prev)}
        >
          Filter one time tasks
        </Button>
        <Button
          type="default"
          style={filterToggleStyle(filterUnassignedTasks)}
          onClick={() => setFilterUnassignedTasks((prev) => !prev)}
        >
          Unassigned tasks
        </Button>
        <Button
          type="default"
          style={filterToggleStyle(filterPendingTasks)}
          onClick={() => setFilterPendingTasks((prev) => !prev)}
        >
          Pending tasks
        </Button>
        <Button
          type="primary"
          className="!font-medium"
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create one-time or unscheduled task
        </Button>
      </div>

      <TaskScheduleTable
        machinesData={machinesData}
        allUsers={allUsers}
        transformedUsers={transformedUsers}
        taskIsNull={filterOneTimeTasks ? true : undefined}
        userIsNull={filterUnassignedTasks ? true : undefined}
        externalStatusValues={filterPendingTasks ? ["PENDING"] : undefined}
        urlInitialUserName={urlUserForTable}
        urlInitialMachineIds={machineIdsFromUrl}
      />

      <CreateTaskExecutionModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        allUsers={transformedUsers}
        machinesData={machinesData}
      />
    </Page>
  );
};

export { TaskHubUserAllTasks };
